/**
 * Guía de caja: pagos y verificación (AUTONOMY · Layer 3).
 */
import { msg } from "@lingui/core/macro";
import { Wallet } from "lucide-solid";

import type { HelpGuide } from "../types";
import { Bullets, GoTo, Note, P, Steps } from "../components/help-prose";

export const financeGuide: HelpGuide = {
  key: "help.finance",
  slug: "caja",
  title: msg`Cashier guide: payments and verification`,
  description: msg`Record tuition payments in bolívares or dollars, verify them, manage the daily exchange rate, plans, discounts and receipts.`,
  audience: msg`Cashier staff and billing administrators`,
  icon: Wallet,
  sections: [
    {
      id: "panorama",
      title: msg`Dual-currency billing`,
      summary: msg`All prices are stored in USD; amounts in VES are always derived from the exchange rate captured on the day of the operation.`,
      body: () => (
        <>
          <P
            msg={msg`Invoices show debt in both currencies. When a payment arrives in bolívares, the rate of that day is attached to the payment, so history never changes when the rate moves.`}
          />
          <GoTo to="/finance" label={msg`Open Finance`} />
        </>
      ),
    },
    {
      id: "registrar-pagos",
      title: msg`Recording a payment`,
      summary: msg`Cashier staff record payments with method, currency, reference number and an optional receipt photo. Recording does not verify.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Open Finance → Payments and choose the student or invoice.`,
              msg`Pick the payment method (cash, pago móvil, transfer, Zelle or Stripe) — only methods enabled by your academy appear.`,
              msg`Enter amount, currency and reference; attach the receipt photo if there is one.`,
            ]}
          />
          <Note
            title={msg`Separation of duties`}
            msg={msg`Staff record payments; only billing admins verify them. A verified payment becomes immutable — corrections are made with refunds.`}
          />
          <GoTo to="/finance/payments" label={msg`Open Payments`} />
        </>
      ),
    },
    {
      id: "verificacion",
      title: msg`Verification queue`,
      summary: msg`Billing admins review pending payments and verify or reject them with a reason. Every decision is recorded in the activity log.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Open Admin → Billing to see the pending queue.`,
              msg`Check the reference and receipt photo against your bank.`,
              msg`Verify to apply the payment to its invoices, or reject with a reason so the cashier can correct it.`,
            ]}
          />
          <GoTo to="/admin/billing" label={msg`Open the verification queue`} />
        </>
      ),
    },
    {
      id: "tasa",
      title: msg`Exchange rate of the day`,
      summary: msg`Register the daily VES/USD rate manually or fetch the official BCV rate with one tap. The whole app converts with the latest rate.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`One rate per day per academy is kept in history — payments reference the rate row that was current when they happened.`,
              msg`"Fetch BCV" pulls the official rate through the configured integration; you can always type it manually.`,
            ]}
          />
          <GoTo to="/finance/rates" label={msg`Open Rates`} />
        </>
      ),
    },
    {
      id: "planes",
      title: msg`Invoices and price plans`,
      summary: msg`Price plans define what a cohort or program costs: one-time, monthly, or split into installments. Enrolling generates the invoices.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Create plans from Admin → Billing → Configuration: amount in USD, optional enrollment fee, and number of installments.`,
              msg`Generating invoices for an enrollment creates one invoice per installment with its due date.`,
              msg`Overdue invoices are highlighted and can be collected via a WhatsApp link with one tap.`,
            ]}
          />
          <GoTo to="/finance/invoices" label={msg`Open Invoices`} />
        </>
      ),
    },
    {
      id: "descuentos",
      title: msg`Discounts and scholarships`,
      summary: msg`Promo codes and scholarships reduce invoices by a percentage or a fixed USD amount, with optional validity window and usage cap.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Create a discount in Admin → Billing → Configuration: choose promo code or scholarship, percent or fixed amount.`,
              msg`Optionally set a validity window and a maximum number of uses.`,
              msg`Deactivate a discount at any time — existing invoices keep what was already applied.`,
            ]}
          />
          <GoTo to="/finance/settings" label={msg`Open Billing configuration`} />
        </>
      ),
    },
    {
      id: "configuracion",
      title: msg`Billing configuration`,
      summary: msg`Choose which payment methods your academy accepts and the footer text printed on receipts — no developer needed.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Payment methods: enable or disable cash, pago móvil, transfer, Zelle and Stripe per academy; disabled methods disappear from the cashier form.`,
              msg`Receipt footer: a custom line (tax info, thank-you note) printed on every receipt, plus the academy public name from Branding.`,
              msg`Stripe online checkout requires the Stripe integration configured by a platform admin.`,
            ]}
          />
          <GoTo to="/finance/settings" label={msg`Open Billing configuration`} />
        </>
      ),
    },
    // B1 · titularidad: la decisión de dinero con más consecuencias para el
    // alumno. Va en la guía de CAJA porque quien la toma es quien cobra.
    {
      id: "titularidad",
      title: msg`What happens if a student hasn't paid`,
      summary: msg`Configure it once for the whole academy, or per course, per cohort and even for one student. Grades and attendance are never withheld.`,
      body: () => (
        <>
          <P
            msg={msg`Pick a preset by name — nothing to check box by box. The recommended one blocks what the server can really take back (new content, today's live class and the certificate) and leaves what the student already paid for.`}
          />
          <Bullets
            items={[
              msg`Inheritance: platform → academy → program → course → cohort → student. The most specific level wins; a "locked" level cannot be loosened from below.`,
              msg`Grace days: nothing is withheld until the grace period after the due date runs out.`,
              msg`A payment awaiting verification suspends the block. Staff record, admins verify — with zero grace you would block students who already paid.`,
              msg`Minimum debt: cents left over from rounding never block anybody.`,
              msg`Exceptions: only the owner or an academy administrator can grant them, always with a reason and an expiry date, and every grant is audited.`,
            ]}
          />
          <Note
            title={msg`Nothing happens until you turn it on`}
            msg={msg`Entitlement ships switched off. While it is off the server calculates and explains, but blocks nobody. Use "Who this would affect" to see the result before enabling it.`}
          />
          <GoTo to="/academy/entitlement" label={msg`Open Entitlement`} />
        </>
      ),
    },
    // B3 · El kit de pasarelas: cómo se OPERA una pasarela, no cómo se programa.
    {
      id: "pasarelas",
      title: msg`Payment gateways: turning one off without losing money`,
      summary: msg`Draining stops new checkouts and lets what is already in flight land. Turning a gateway off never discards a webhook — the money already moved on the provider's side.`,
      body: () => (
        <>
          <P
            msg={msg`When a gateway starts failing, the question that matters is how much money is halfway through. Admin → Billing → Gateways answers it: state, payments in flight and how old the oldest one is.`}
          />
          <Bullets
            items={[
              msg`Active: students can pay and incoming confirmations settle normally.`,
              msg`Draining: no new checkout is created, but confirmations still settle. This is what you want in an incident.`,
              msg`Off: only after everything has drained. Incoming events are still stored — an administrator settles them by hand and the reason is audited.`,
              msg`A gateway your academy simply does not offer is a different switch: Configuration → payment methods.`,
              msg`Every signed event is stored before it is applied, so nothing is ever lost; what could not be applied waits in the webhook queue.`,
            ]}
          />
          <Note
            title={msg`Refunds now really give the money back`}
            msg={msg`For gateways that support it, a refund is executed with the provider before the books are updated. If the provider rejects it, nothing is reversed and the refund is left for review instead of silently claiming to be done.`}
          />
          <GoTo to="/admin/billing" label={msg`Open Gateways`} />
        </>
      ),
    },
    // B4 · G3/G5 — prepago con descuento.
    {
      id: "prepago",
      title: msg`Pay ahead and save`,
      summary: msg`Students who already paid their first month can settle the remaining instalments in one payment with a discount. It is the academy's cash flow lever.`,
      body: () => (
        <>
          <P
            msg={msg`The offer appears on its own: the server checks that the first instalment is settled and that there are enough upcoming ones left. There is no coupon to hand out and nothing for the student to type.`}
          />
          <Bullets
            items={[
              msg`The discount is tiered by how many instalments are brought forward — more months, bigger discount.`,
              msg`Accepting issues one invoice and voids the instalments it replaces, atomically and with an audit trail.`,
              msg`The amount is based on what is still owed, so an instalment already partly paid is never charged twice.`,
              msg`It is committed in USD: the bolívar figure shown follows today's rate and does not change what was agreed.`,
              msg`Cash position (Gateways tab) shows what was collected in advance, what is still projected and what the discount cost.`,
            ]}
          />
          <GoTo to="/payments" label={msg`Open My payments`} />
        </>
      ),
    },
    {
      id: "recibos",
      title: msg`Printable receipts`,
      summary: msg`Every payment has a printable receipt with the academy's name, the amounts in both currencies and the rate used.`,
      body: () => (
        <>
          <P
            msg={msg`Open a payment and use the receipt button to get a print-ready page. The receipt footer and public name are configurable in Billing configuration and Branding.`}
          />
          <GoTo to="/finance/payments" label={msg`Open Payments`} />
        </>
      ),
    },
  ],
};
